// Geometry measured off the slice PNGs: part `p` fills the wedge ending at 12 o'clock after
// (p - 1) steps of 360/denominator counter-clockwise, the art reaches r=64, and its straight cuts
// sit 1.25 in from where the pizza was divided. That gap is a constant sideways offset, not an
// angle, so the cuts stay parallel all the way in rather than converging on the middle.
const VIEWBOX_SIZE = 128
const CENTER = VIEWBOX_SIZE / 2
const ART_RADIUS = 64
const EDGE_GAP = 1.25
const STROKE_WIDTH = 3
// The stroke is centred on the line it draws, so pull it half a width inside the slice footprint on
// every side. Otherwise two outlines either side of a divide overlap and fuse into one line.
const ARC_RADIUS = ART_RADIUS - STROKE_WIDTH / 2
const CUT_INSET = EDGE_GAP + STROKE_WIDTH / 2
const OUTER_ALONG = Math.sqrt(ARC_RADIUS ** 2 - CUT_INSET ** 2)

/**
 * Where the cuts stop short of the middle: the point their inset lines converge on. That is further
 * out the narrower the slice, which is why the art's own tips are blunter the more the pizza is cut up.
 */
function tipAlong(denominator: number): number {
  return CUT_INSET / Math.tan(Math.PI / denominator)
}

interface Cut {
  degrees: number
  sideways: number
}

interface Props {
  denominator: number
  /** Whether the counted slices have pizza drawn on them, which decides how much of the wedge to trace. */
  hasSliceArt: boolean
  parts: number[]
}

/** A point on the cut at `degrees`, `along` out from the middle and `sideways` off it (+ is clockwise). */
function cutPoint(degrees: number, along: number, sideways: number): string {
  const radians = (degrees * Math.PI) / 180
  const sin = Math.sin(radians)
  const cos = Math.cos(radians)
  return `${CENTER + along * sin + sideways * cos} ${CENTER - along * cos + sideways * sin}`
}

/**
 * The two cuts bounding a part. The stroke's outer edge lands where the slice art's own edge is, so an
 * outlined empty wedge fills exactly its slice's footprint and every gap between wedges matches.
 */
function cuts(denominator: number, part: number): [Cut, Cut] {
  const sliceDegrees = 360 / denominator
  return [
    { degrees: (denominator - part) * sliceDegrees, sideways: CUT_INSET },
    { degrees: (denominator - part + 1) * sliceDegrees, sideways: -CUT_INSET },
  ]
}

function arc(from: Cut, to: Cut): string {
  return `M ${cutPoint(from.degrees, OUTER_ALONG, from.sideways)} A ${ARC_RADIUS} ${ARC_RADIUS} 0 0 1 ${cutPoint(
    to.degrees,
    OUTER_ALONG,
    to.sideways,
  )}`
}

/** The whole wedge: an empty slice has no art of its own, so the outline has to define it. */
function wedgePath(denominator: number, part: number): string {
  const [from, to] = cuts(denominator, part)
  const tip = tipAlong(denominator)
  return `M ${cutPoint(from.degrees, tip, from.sideways)} L ${cutPoint(from.degrees, OUTER_ALONG, from.sideways)}
    A ${ARC_RADIUS} ${ARC_RADIUS} 0 0 1 ${cutPoint(to.degrees, OUTER_ALONG, to.sideways)}
    L ${cutPoint(to.degrees, tip, to.sideways)} Z`
}

/**
 * The crust, plus any cut facing empty space. The slice art already draws the cuts it shares with a
 * neighbouring slice, so redrawing those cages the figure — but a cut with nothing beside it is invisible.
 */
function crustPath(denominator: number, part: number, counted: number[]): string {
  const [from, to] = cuts(denominator, part)
  const tip = tipAlong(denominator)
  const wrap = (neighbour: number): number => ((neighbour - 1 + denominator) % denominator) + 1
  const edges = [
    { cut: from, neighbour: wrap(part + 1) },
    { cut: to, neighbour: wrap(part - 1) },
  ].filter(({ neighbour }) => !counted.includes(neighbour))

  return [
    arc(from, to),
    ...edges.map(
      ({ cut }) =>
        `M ${cutPoint(cut.degrees, OUTER_ALONG, cut.sideways)} L ${cutPoint(cut.degrees, tip, cut.sideways)}`,
    ),
  ].join(' ')
}

export default function CountedSlicesOutline({ denominator, hasSliceArt, parts }: Props): React.ReactElement {
  return (
    <svg
      aria-hidden
      className="absolute inset-0 pointer-events-none text-purple"
      data-test-id="counted-slices-outline"
      fill="none"
      height={VIEWBOX_SIZE}
      viewBox={`0 0 ${VIEWBOX_SIZE} ${VIEWBOX_SIZE}`}
      width={VIEWBOX_SIZE}
    >
      {parts.map((part) => (
        <path
          d={hasSliceArt ? crustPath(denominator, part, parts) : wedgePath(denominator, part)}
          key={part}
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={STROKE_WIDTH}
        />
      ))}
    </svg>
  )
}
